import * as React from "react";
import {  useEffect, useRef } from "react";
import {
  makeStyles,
  Text,
  Spinner,
  Button,
  Dropdown,
  Option,
  OptionOnSelectData,
  SelectionEvents,
} from "@fluentui/react-components";
import ChatMessage from "./ChatMessage";
import ChatInputArea from "./ChatInputArea";
import { SignOutRegular, AddRegular } from "@fluentui/react-icons";
import { ChatSummary } from "../App";

// Define brand color
const brandColor = "#921784";

// --- Styles ---
const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: "#ffffff",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ['5px', '10px'],
    borderBottom: '1px solid #e0e0e0',
    flexShrink: 0,
    gap: '10px',
  },
  chatSelectorContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexGrow: 1,
    minWidth: '150px',
  },
  messageArea: {
    flexGrow: 1,
    overflowY: "auto",
    padding: "15px",
    display: "flex",
    flexDirection: "column",
  },
  initialPromptContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    gap: '15px',
    padding: '20px',
  },
  initialPromptText: {
    fontSize: '18px',
    fontWeight: '600',
    color: brandColor,
  },
  inputAreaContainer: {
    flexShrink: 0,
  },
});

// --- Props Interface ---
export interface Message {
  id: string | number;
  sender: "user" | "bot";
  text: string;
}

export interface ChatInterfaceProps {
  initialMessages?: Message[];
  onSendMessage: (messageText: string) => Promise<void>;
  isLoading: boolean;
  onLogout: () => void;
  chatsList: ChatSummary[]; 
  currentChatId: number | null; 
  onSelectChat: (chatId: number | null) => void;
  onNewChat: () => void;
  copyAndPaste: (text: string) => void;
}

// --- Component ChatInterface ---
const ChatInterface: React.FC<ChatInterfaceProps> = ({
  initialMessages = [],
  onSendMessage,
  isLoading,
  onLogout,
  chatsList,
  currentChatId,
  onSelectChat,
  onNewChat,
  copyAndPaste
}) => {
  const styles = useStyles();
  const messageAreaRef = useRef<HTMLDivElement>(null);

  // --- Auto-scroll ---
  useEffect(() => {
    const messageArea = messageAreaRef.current;
    if (messageArea) {
      messageArea.scrollTop = messageArea.scrollHeight;
    }
  }, [initialMessages, isLoading]);

  // --- Handle Sending ---
  const handleSend = async (messageText: string) => {
    try {
      await onSendMessage(messageText);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleDropdownSelect = (
    _event: SelectionEvents,
    data: OptionOnSelectData
  ) => {
    const selectedIdString = data.optionValue;
    const selectedId = selectedIdString ? parseInt(selectedIdString, 10) : null;
    console.log("Dropdown selection changed:", selectedId);

    if (selectedId !== null && !isNaN(selectedId)) {
      onSelectChat(selectedId);
    } else if (selectedIdString === "") {
      console.log("Opción 'Iniciar Nueva Conversación' seleccionada.");
      onNewChat();
    } else {
      console.warn("Invalid chat ID selected from dropdown:", selectedIdString);
    }
  };

  // --- Render ---
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.chatSelectorContainer}>
          <Button
            icon={<AddRegular />}
            size="small"
            appearance="subtle"
            onClick={onNewChat}
            aria-label="Nuevo chat"
            title="Nuevo chat"
          />
          <Dropdown
            placeholder={currentChatId === null ? "Iniciar conversación" : (chatsList.find(c=>c.id === currentChatId)?.name || "Seleccionar chat...")}
            selectedOptions={currentChatId ? [String(currentChatId)] : []}
            value={currentChatId ? chatsList.find(c=>c.id === currentChatId)?.name : undefined}
            onOptionSelect={handleDropdownSelect}
            style={{ minWidth: '150px', flexGrow: 1 }}
          >
            {currentChatId === null && <Option key="new-chat-placeholder" value="" disabled>Iniciar conversación</Option>}
            {chatsList.map((chat) => (
              <Option key={chat.id} value={String(chat.id)} text={chat.name}>
                {chat.name.length > 30 ? `${chat.name.substring(0, 27)}...` : chat.name}
              </Option>
            ))}
          </Dropdown>
        </div>
          <Button
            appearance="transparent"
            icon={<SignOutRegular />}
            onClick={onLogout}
            aria-label="Cerrar sesión"
          >
            Cerrar Sesión
          </Button>
      </div>

      <div className={styles.messageArea} ref={messageAreaRef}>
        {currentChatId === null && initialMessages.length === 0 && !isLoading && (
          <div className={styles.initialPromptContainer}>
            <Text as="h3" className={styles.initialPromptText}>¿Cómo te puedo ayudar?</Text>
          </div>
        )}
        {initialMessages.map((msg) => (
          <ChatMessage 
            key={msg.id} 
            sender={msg.sender} 
            text={msg.text} 
            copyAndPaste={copyAndPaste}
            />
        ))}
        {isLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
            <Spinner size="small" label="Lexfania está pensando..." labelPosition="after" />
          </div>
        )}
      </div>

      <div className={styles.inputAreaContainer}>
        <ChatInputArea onSendMessage={handleSend} isLoading={isLoading} />
      </div>
    </div>
  );
};

export default ChatInterface;
